import { useMemo, useState } from "react";
import { empfehlung, FAELLE } from "@/lib/teppich";
import { trackSettled, track } from "@/lib/track";

const FALL_LABEL = {
  esstisch: "Esstisch",
  sofa: "Sofa / Couch",
  wohnzimmer: "Wohnzimmer",
  bett: "Bett",
};

const DEFAULTS = {
  esstisch: { laenge: 160, breite: 90 },
  sofa: { breite: 220, variante: "vorderfuesse" },
  wohnzimmer: { laenge: 500, breite: 400 },
  bett: { breite: 180, laenge: 200, variante: "quer" },
};

function NumberField({ label, value, onChange, suffix = "cm" }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center rounded-lg border bg-background focus-within:ring-2 focus-within:ring-primary/40">
        <input
          type="number"
          inputMode="numeric"
          min={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full bg-transparent px-3 py-2 outline-none"
        />
        <span className="px-3 text-sm text-muted-foreground">{suffix}</span>
      </div>
    </label>
  );
}

function Choice({ label, options, value, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium">{label}</span>
      <div className="inline-flex rounded-lg border p-0.5 bg-muted/40 w-fit">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              value === o.value ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Liefert die Szene (in cm) für die Draufsicht-Skizze.
function buildScene(fall, inputs, res) {
  if (res.mode === "laeufer") {
    const bw = inputs.breite;
    const bl = inputs.laenge;
    const rw = res.laeufer.breite;
    const gap = 15;
    const w = bw + 2 * (rw + gap);
    return {
      w,
      h: bl,
      els: [
        { x: rw + gap, y: 0, w: bw, h: bl, role: "furniture", label: "Bett" },
        { x: 0, y: 0, w: rw, h: bl, role: "rug" },
        { x: bw + rw + 2 * gap, y: 0, w: rw, h: bl, role: "rug", label: "Läufer" },
      ],
    };
  }
  const rugL = res.groesse.lang;
  const rugK = res.groesse.kurz;
  if (fall === "esstisch") {
    const tl = Math.max(inputs.laenge, inputs.breite);
    const tk = Math.min(inputs.laenge, inputs.breite);
    return {
      w: rugL,
      h: rugK,
      els: [
        { x: 0, y: 0, w: rugL, h: rugK, role: "rug" },
        { x: (rugL - tl) / 2, y: (rugK - tk) / 2, w: tl, h: tk, role: "furniture", label: "Tisch" },
      ],
    };
  }
  if (fall === "sofa") {
    const sb = inputs.breite;
    const sd = 90; // Sofatiefe für die Skizze
    if (inputs.variante === "komplett") {
      return {
        w: rugL,
        h: rugK,
        els: [
          { x: 0, y: 0, w: rugL, h: rugK, role: "rug" },
          { x: (rugL - sb) / 2, y: rugK * 0.18, w: sb, h: sd, role: "furniture", label: "Sofa" },
        ],
      };
    }
    // vordere Füße: Sofa oben, nur unterer Streifen auf dem Teppich
    const w = Math.max(rugL, sb);
    const overlap = 22;
    return {
      w,
      h: sd + rugK - overlap,
      els: [
        { x: (w - rugL) / 2, y: sd - overlap, w: rugL, h: rugK, role: "rug" },
        { x: (w - sb) / 2, y: 0, w: sb, h: sd, role: "furniture", label: "Sofa" },
      ],
    };
  }
  if (fall === "wohnzimmer") {
    const roomL = Math.max(inputs.laenge, inputs.breite);
    const roomK = Math.min(inputs.laenge, inputs.breite);
    return {
      w: roomL,
      h: roomK,
      els: [
        { x: 0, y: 0, w: roomL, h: roomK, role: "room", label: "Raum" },
        { x: (roomL - rugL) / 2, y: (roomK - rugK) / 2, w: rugL, h: rugK, role: "rug" },
      ],
    };
  }
  // bett quer
  const bw = inputs.breite;
  const bl = inputs.laenge;
  const w = Math.max(rugL, bw);
  const h = bl + 50;
  return {
    w,
    h,
    els: [
      { x: (w - rugL) / 2, y: h - rugK, w: rugL, h: rugK, role: "rug" },
      { x: (w - bw) / 2, y: 0, w: bw, h: bl, role: "furniture", label: "Bett" },
    ],
  };
}

function Skizze({ fall, inputs, res }) {
  const scene = buildScene(fall, inputs, res);
  const pad = scene.w * 0.06 + 12;
  const vbW = scene.w + 2 * pad;
  const vbH = scene.h + 2 * pad;
  const fill = { rug: "hsl(173 58% 39% / 0.22)", furniture: "transparent", room: "transparent" };
  const stroke = { rug: "hsl(173 58% 39%)", furniture: "hsl(222 16% 28%)", room: "hsl(222 16% 28% / 0.5)" };
  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      className="w-full h-auto max-h-[320px]"
      role="img"
      aria-label="Maßstabsgetreue Skizze von Teppich und Möbel"
    >
      <g transform={`translate(${pad} ${pad})`}>
        {scene.els.map((e, i) => (
          <g key={i}>
            <rect
              x={e.x}
              y={e.y}
              width={e.w}
              height={e.h}
              rx={Math.min(e.w, e.h) * 0.04}
              fill={fill[e.role]}
              stroke={stroke[e.role]}
              strokeWidth={vbW * 0.004}
              strokeDasharray={e.role === "room" ? `${vbW * 0.02} ${vbW * 0.015}` : undefined}
            />
            {e.label && (
              <text
                x={e.x + e.w / 2}
                y={e.y + e.h / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={vbW * 0.035}
                fill={e.role === "rug" ? stroke.rug : "hsl(222 16% 28%)"}
              >
                {e.label}
              </text>
            )}
          </g>
        ))}
      </g>
    </svg>
  );
}

export default function TeppichRechner({ fall: initialFall = "esstisch" }) {
  const [fall, setFall] = useState(FAELLE.includes(initialFall) ? initialFall : "esstisch");
  const [inputs, setInputs] = useState(DEFAULTS);

  const cur = inputs[fall];
  const res = useMemo(() => empfehlung(fall, cur), [fall, cur]);

  const set = (patch) => {
    setInputs((prev) => ({ ...prev, [fall]: { ...prev[fall], ...patch } }));
    trackSettled("teppich_berechnet", { calculator: fall });
  };
  const switchFall = (f) => {
    setFall(f);
    track("teppich_fall_gewählt", { calculator: f });
  };

  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-6 shadow-sm">
      {/* Fall-Auswahl */}
      <div className="flex flex-wrap gap-2 mb-5">
        {FAELLE.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => switchFall(f)}
            data-testid={`fall-${f}`}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
              fall === f ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground hover:border-primary/50"
            }`}
          >
            {FALL_LABEL[f]}
          </button>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Eingaben */}
        <div className="flex flex-col gap-4">
          {fall === "esstisch" && (
            <>
              <NumberField label="Tischlänge" value={cur.laenge} onChange={(v) => set({ laenge: v })} />
              <NumberField label="Tischbreite" value={cur.breite} onChange={(v) => set({ breite: v })} />
            </>
          )}
          {fall === "sofa" && (
            <>
              <NumberField label="Sofabreite" value={cur.breite} onChange={(v) => set({ breite: v })} />
              <Choice
                label="Platzierung"
                value={cur.variante}
                onChange={(v) => set({ variante: v })}
                options={[
                  { value: "vorderfuesse", label: "Vordere Füße drauf" },
                  { value: "komplett", label: "Komplett drauf" },
                ]}
              />
            </>
          )}
          {fall === "wohnzimmer" && (
            <>
              <NumberField label="Raumlänge" value={cur.laenge} onChange={(v) => set({ laenge: v })} />
              <NumberField label="Raumbreite" value={cur.breite} onChange={(v) => set({ breite: v })} />
            </>
          )}
          {fall === "bett" && (
            <>
              <NumberField label="Bettbreite" value={cur.breite} onChange={(v) => set({ breite: v })} />
              <NumberField label="Bettlänge" value={cur.laenge} onChange={(v) => set({ laenge: v })} />
              <Choice
                label="Variante"
                value={cur.variante}
                onChange={(v) => set({ variante: v })}
                options={[
                  { value: "quer", label: "Ein Teppich" },
                  { value: "laeufer", label: "Zwei Läufer" },
                ]}
              />
            </>
          )}
        </div>

        {/* Ergebnis */}
        <div className="flex flex-col gap-3">
          <div className="rounded-xl bg-secondary/60 p-4">
            <div className="text-sm text-muted-foreground">Empfohlene Teppichgröße</div>
            <div className="text-2xl font-semibold mt-0.5" data-testid="teppich-ergebnis">{res.label}</div>
            {res.mode === "rect" && (
              <div className="text-sm text-muted-foreground mt-1">
                Errechnet: {res.ziel.kurz} × {res.ziel.lang} cm, aufgerundet auf die nächste handelsübliche Größe.
              </div>
            )}
          </div>
          <Skizze fall={fall} inputs={cur} res={res} />
          <p className="text-sm text-muted-foreground leading-relaxed">{res.hinweis}</p>
        </div>
      </div>
    </div>
  );
}
